import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Plus, ExternalLink, Copy, Pencil, Trash2, ToggleLeft, ToggleRight, BarChart2, ClipboardList } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import DeleteConfirmDialog from "../../components/DeleteConfirmDialog";
import { useAppearance } from "../../contexts/AppearanceContext";
import { toast } from "../../components/ui/sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PUBLIC_BASE = window.location.origin;

export default function FormsPage() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const navigate = useNavigate();
  const appearance = useAppearance();
  const tc500 = appearance.colors?.["500"] || "#3B82F6";

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/forms`, { withCredentials: true });
      setForms(res.data);
    } catch { /* silent */ }
    setLoading(false);
  };

  useEffect(() => { fetch(); }, []);

  const handleNew = async () => {
    const res = await axios.post(`${API}/forms`, { name: "Novo Formulário" }, { withCredentials: true });
    navigate(`/configuracoes/formularios/${res.data.id}`);
  };

  const toggleActive = async (form) => {
    await axios.put(`${API}/forms/${form.id}`, { active: !form.active }, { withCredentials: true });
    setForms(prev => prev.map(f => f.id === form.id ? { ...f, active: !f.active } : f));
    toast.success(form.active ? "Formulário desativado." : "Formulário ativado.");
  };

  const handleDuplicate = async (form) => {
    const res = await axios.post(`${API}/forms/${form.id}/duplicate`, {}, { withCredentials: true });
    setForms(prev => [res.data, ...prev]);
    toast.success("Formulário duplicado.");
  };

  const confirmDelete = async () => {
    await axios.delete(`${API}/forms/${deleteTarget.id}`, { withCredentials: true });
    setForms(prev => prev.filter(f => f.id !== deleteTarget.id));
    setDeleteTarget(null);
    toast.success("Formulário excluído.");
  };

  const copyLink = (slug) => {
    navigator.clipboard.writeText(`${PUBLIC_BASE}/f/${slug}`);
    toast.success("Link copiado!");
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tc500}18` }}>
            <ClipboardList className="w-5 h-5" style={{ color: tc500 }} />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-heading text-slate-900">Formulários</h1>
            <p className="text-slate-500 text-sm">Capture leads via links públicos personalizados.</p>
          </div>
        </div>
        <Button onClick={handleNew} className="gap-2 text-white" style={{ backgroundColor: tc500 }}>
          <Plus className="w-4 h-4" />Novo Formulário
        </Button>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-16 text-slate-400">
          <div className="animate-spin w-5 h-5 border-2 rounded-full mr-2" style={{ borderColor: `${tc500}40`, borderTopColor: tc500 }} />
          Carregando...
        </div>
      ) : forms.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-xl">
          <ClipboardList className="w-10 h-10 mx-auto mb-3 text-slate-200" />
          <p className="text-sm font-medium text-slate-500">Nenhum formulário criado.</p>
          <p className="text-xs text-slate-400 mt-1 mb-4">Crie seu primeiro formulário para capturar leads automaticamente.</p>
          <Button onClick={handleNew} variant="outline" size="sm" className="gap-1.5">
            <Plus className="w-3.5 h-3.5" />Criar formulário
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {forms.map(form => (
            <div key={form.id}
              onClick={() => navigate(`/configuracoes/formularios/${form.id}`)}
              className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-4 hover:shadow-sm hover:border-slate-300 transition-all cursor-pointer">

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="text-sm font-semibold text-slate-800 truncate">{form.name}</p>
                </div>
                <p className="text-xs text-slate-400 font-mono truncate">/f/{form.slug}</p>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-4 flex-shrink-0 text-xs text-slate-500">
                <span className="flex items-center gap-1"><BarChart2 className="w-3.5 h-3.5" />{form.views ?? 0} visitas</span>
                <span className="flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" />{form.submissions ?? 0} envios</span>
                {(form.views ?? 0) > 0 && (
                  <span className="text-emerald-600 font-medium">
                    {Math.round((form.submissions ?? 0) / (form.views ?? 1) * 100)}% conv.
                  </span>
                )}
              </div>

              {/* Actions — stop propagation so clicks don't navigate */}
              <div className="flex items-center gap-1 flex-shrink-0" onClick={e => e.stopPropagation()}>
                {/* Toggle ativo/inativo inline */}
                <button onClick={() => toggleActive(form)} title={form.active ? "Desativar" : "Ativar"}
                  className="p-2 rounded-lg hover:bg-slate-100 transition-colors">
                  {form.active
                    ? <ToggleRight className="w-5 h-5" style={{ color: tc500 }} />
                    : <ToggleLeft className="w-5 h-5 text-slate-300" />}
                </button>
                {/* Abrir formulário */}
                <a href={`/f/${form.slug}`} target="_blank" rel="noreferrer" title="Abrir formulário"
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors">
                  <ExternalLink className="w-4 h-4" />
                </a>
                {/* Duplicar */}
                <button onClick={() => handleDuplicate(form)} title="Duplicar"
                  className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-700 transition-colors">
                  <Copy className="w-4 h-4" />
                </button>
                {/* Excluir */}
                <button onClick={() => setDeleteTarget(form)} title="Excluir"
                  className="p-2 rounded-lg hover:bg-rose-50 text-slate-500 hover:text-rose-500 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Excluir formulário"
        description={`Tem certeza que deseja excluir "${deleteTarget?.name}"? Esta ação não pode ser desfeita.`}
        onConfirm={confirmDelete}
      />
    </div>
  );
}
